import { useQuery } from '@tanstack/react-query';
import { getAuth } from 'firebase/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// ============================================================================
// Types
// ============================================================================

export interface BillableItemFile {
  id: string;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_by: string;
  created_at: string;
}

// ============================================================================
// Auth helper (matches BillableItemReceiptUpload pattern)
// ============================================================================

async function getAuthToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch receipt files for a billable item.
 * Used on the invoice detail page to show receipt indicators and file lists.
 */
export function useBillableItemFiles(billableItemId: string | null | undefined) {
  return useQuery({
    queryKey: ['billable-item-files', billableItemId],
    queryFn: async () => {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/billable-items/${billableItemId}/files`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load files');
      const data = await res.json();
      return (data.files || []) as BillableItemFile[];
    },
    enabled: !!billableItemId,
  });
}

/**
 * Fetch receipt file counts for multiple billable items at once.
 * Returns a map of billableItemId -> file count.
 * Used on the invoice detail page to show receipt indicators without N+1 queries.
 */
export function useBillableItemFileCounts(billableItemIds: string[]) {
  return useQuery({
    queryKey: ['billable-item-file-counts', ...billableItemIds.sort()],
    queryFn: async () => {
      const token = await getAuthToken();
      const counts: Record<string, number> = {};

      // Fetch files for each billable item in parallel
      await Promise.all(
        billableItemIds.map(async (id) => {
          try {
            const res = await fetch(`${API_BASE_URL}/api/billable-items/${id}/files`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              counts[id] = (data.files || []).length;
            } else {
              counts[id] = 0;
            }
          } catch {
            counts[id] = 0;
          }
        })
      );

      return counts;
    },
    enabled: billableItemIds.length > 0,
    // Cache for 2 minutes since file counts don't change frequently
    staleTime: 2 * 60 * 1000,
  });
}

// ============================================================================
// Download helper
// ============================================================================

/**
 * Download a receipt file by file ID.
 * Triggers a browser download with the original filename.
 */
export async function downloadBillableItemFile(fileId: string, fileName: string): Promise<void> {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE_URL}/api/billable-item-files/${fileId}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
